import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

/**
 * Provides functionality for a simple paginated navigation.
 * It uses a 2-way binding for `pageIndex`.
 *
 * @example
 * ```html
 * <app-pagination
 *   [totalPages]="10"
 *   [(pageIndex)]="0"
 *   (pageIndexChange)="onPageIndexChange($event)">
 * </app-pagination>
 * ```
 */
@Component({
    selector: 'app-pagination',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './pagination.component.html',
    styleUrl: './pagination.component.scss'
})
export class PaginationComponent implements OnInit {
    @Input()
    totalPages = 0;

    @Input()
    pageIndex = 0;

    @Output()
    readonly pageIndexChange = new EventEmitter<number>();

    protected currentPageIndex!: number;

    /**
     * Validates the initial values of input variables
     */
    ngOnInit(): void {
        if (this.totalPages <= 0) {
            // eslint-disable-next-line no-console
            console.error('totalPages must be greater than 0');
        }

        if (this.pageIndex > this.totalPages - 1) {
            // eslint-disable-next-line no-console
            console.error('pageIndex must be less than totalPages');
        }
    }

    /**
     * Updates the `pageIndex` based on the specified direction by normalizing it
     * and emits the `pageIndexChange` event if the page index has actually changed.
     */
    protected onClick(direction: -1 | 1): void {
        const oldValue = this.pageIndex;
        this.pageIndex = this.normalizePageIndex(this.pageIndex + direction);

        if (this.pageIndex !== oldValue) {
            this.pageIndexChange.emit(this.pageIndex);
        }
    }

    /**
     * Normalizes the given page index to ensure it falls within the valid range of pages.
     */
    private normalizePageIndex(pageIndex: number): number {
        const min = 0;
        const max = this.totalPages - 1;

        return Math.max(min, Math.min(max, pageIndex));
    }
}
